/**
 * DOM manipulation utilities for the DynamicCriteriaGenerator editor.
 *
 * These are pure functions that handle contentEditable DOM operations
 * like cursor positioning, text manipulation, and range operations.
 */

/**
 * Moves the cursor to the end of the given element.
 */
export function moveCursorToEnd(element: HTMLElement): void {
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(element);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
  element.focus();
}

/**
 * Gets the current cursor position in the editor.
 * Returns null if no valid selection exists.
 */
export function getCursorPosition(): { node: Node; offset: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  return {
    node: range.startContainer,
    offset: range.startOffset,
  };
}

/**
 * Sets the cursor position at a specific node and offset.
 */
export function setCursorPosition(node: Node, offset: number): void {
  const selection = window.getSelection();
  if (!selection) return;

  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * Removes text from a text node between start and end offsets.
 * Returns true if successful, false otherwise.
 */
export function removeTextRange(
  textNode: Node,
  startOffset: number,
  endOffset: number
): boolean {
  if (textNode.nodeType !== Node.TEXT_NODE) {
    return false;
  }

  try {
    const range = document.createRange();
    range.setStart(textNode, startOffset);
    range.setEnd(textNode, endOffset);
    range.deleteContents();

    // Set cursor at the deletion point
    const selection = window.getSelection();
    if (selection) {
      const newRange = document.createRange();
      newRange.setStart(textNode, startOffset);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Finds the end of a "word" starting from a given offset in a text node.
 * A word ends at the next space or end of text.
 */
export function findWordEndOffset(textContent: string, startOffset: number): number {
  let endOffset = startOffset;
  while (endOffset < textContent.length && textContent[endOffset] !== ' ') {
    endOffset++;
  }
  return endOffset;
}

/**
 * Gets the bounding rectangle for a position in a text node.
 * Useful for positioning menus relative to cursor position.
 */
export function getPositionRect(
  textNode: Node,
  offset: number
): DOMRect | null {
  if (textNode.nodeType !== Node.TEXT_NODE) {
    return null;
  }

  try {
    const range = document.createRange();
    range.setStart(textNode, offset);
    range.setEnd(textNode, Math.min(offset + 1, textNode.textContent?.length || 0));
    return range.getBoundingClientRect();
  } catch (e) {
    return null;
  }
}

/**
 * Gets the text node at the current cursor position.
 * If cursor is in an element node, attempts to find the adjacent text node.
 */
export function getTextNodeAtCursor(): { node: Node; offset: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  const range = selection.getRangeAt(0);
  let textNode = range.startContainer;
  let cursorPos = range.startOffset;

  // If cursor is in an element node, try to find the text node
  if (textNode.nodeType !== Node.TEXT_NODE) {
    // Check if we're at the boundary of a text node
    if (textNode.childNodes.length > 0 && cursorPos > 0) {
      const prevNode = textNode.childNodes[cursorPos - 1];
      if (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
        textNode = prevNode;
        cursorPos = prevNode.textContent?.length || 0;
      }
    }
  }

  if (textNode.nodeType !== Node.TEXT_NODE) {
    return null;
  }

  return { node: textNode, offset: cursorPos };
}

/**
 * Finds the position of a "/" character suitable for slash command.
 * Returns -1 if no valid slash is found.
 *
 * A valid slash must be:
 * - At the start of the text, OR
 * - Preceded by a space or special character
 */
export function findSlashPosition(text: string, cursorPos: number): number {
  for (let i = cursorPos - 1; i >= 0; i--) {
    if (text[i] === '/') {
      // Check if there's a space before the slash (or it's at start)
      if (
        i === 0 ||
        text[i - 1] === ' ' ||
        text[i - 1] === '\u00A0' ||
        text[i - 1] === '\u200B'
      ) {
        return i;
      }
    }
    // Stop searching if we hit a space (no slash in current "word")
    if (text[i] === ' ' || text[i] === '\u00A0') {
      break;
    }
  }
  return -1;
}

/**
 * Inserts a pill element at the current cursor position.
 * Handles spacing before and after the pill.
 */
export function insertPillAtCursor(
  pillElement: HTMLElement,
  editor: HTMLElement
): void {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;

  const range = selection.getRangeAt(0).cloneRange();
  range.deleteContents();
  range.insertNode(pillElement);

  // Add space before pill if needed
  const prevSibling = pillElement.previousSibling;
  if (
    !prevSibling ||
    (prevSibling.nodeType === Node.TEXT_NODE &&
      !prevSibling.textContent?.endsWith(' '))
  ) {
    const spaceBefore = document.createTextNode(' ');
    pillElement.parentNode?.insertBefore(spaceBefore, pillElement);
  }

  // Add space after pill
  const spaceAfter = document.createTextNode(' ');
  pillElement.parentNode?.insertBefore(spaceAfter, pillElement.nextSibling);

  // Add zero-width space for cursor anchor
  const cursorAnchor = document.createTextNode('\u200B');
  spaceAfter.parentNode?.insertBefore(cursorAnchor, spaceAfter.nextSibling);

  // Set cursor after the pill
  const newRange = document.createRange();
  newRange.selectNodeContents(cursorAnchor);
  newRange.collapse(false);
  selection.removeAllRanges();
  selection.addRange(newRange);

  editor.focus();
}
